import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Settings, LayoutGrid, X, Sparkles } from 'lucide-react';
import { cn } from '@lib/utils';
import { MODULLER, type ModulKey } from '@lib/modules';
import { useModulAyarlari } from '@features/ayarlar';
import { AiKrediChip } from '@features/ai';
import { ModulSihirbaziModal } from './ModulSihirbaziModal';
import { useAuthStore } from '@/store/auth';

// Route hover prefetch: kullanici linke yaklasinca o sayfa chunk'ini
// arka planda indir. Idle prefetch zaten cogunu yakalar; bu son guvence.
const ROUTE_PREFETCH: Record<string, () => Promise<unknown>> = {
  '/dashboard': () => import('@features/dashboard/DashboardPage'),
  '/musteriler': () => import('@features/musteri/MusteriListPage'),
  '/aktiviteler': () => import('@features/aktivite/AktivitePage'),
  '/firsatlar': () => import('@features/firsat/FirsatKanbanPage'),
  '/takvim': () => import('@features/takvim/TakvimPage'),
  '/urunler': () => import('@features/urun/UrunListPage'),
  '/teklifler': () => import('@features/teklif/TeklifListPage'),
  '/siparisler': () => import('@features/siparis/SiparisListPage'),
  '/talepler': () => import('@features/destek/TalepListPage'),
  '/epostalar': () => import('@features/eposta/EpostaListPage'),
  '/meta-yonetim': () => import('@features/meta/MetaYonetimPage'),
  '/ayarlar': () => import('@features/ayarlar/AyarlarPage'),
  '/ai-sablonlar': () => import('@features/ai/AiSablonlarPage'),
  '/admin/ai-maliyet': () => import('@features/ai/AiMaliyetPage'),
};
const prefetched = new Set<string>();
function prefetchRoute(path: string) {
  if (prefetched.has(path)) return;
  const fn = ROUTE_PREFETCH[path];
  if (!fn) return;
  prefetched.add(path);
  void fn();
}

// modul key -> backend permission resource (talepler menu = "talep" resource)
const MODUL_IZIN: Record<ModulKey, string | null> = {
  dashboard: null, // panel her zaman görünür
  musteri: 'musteri:goruntule',
  aktivite: 'aktivite:goruntule',
  firsat: 'firsat:goruntule',
  teklif: 'teklif:goruntule',
  siparis: 'siparis:goruntule',
  urun: 'urun:goruntule',
  destek: 'talep:goruntule',
  eposta: 'eposta:goruntule',
  meta: 'meta:goruntule',
  takvim: 'takvim:goruntule',
};

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps = {}) {
  const { t } = useTranslation('common');
  const { data: ayarlar } = useModulAyarlari();
  const [sihirbazAcik, setSihirbazAcik] = useState(false);
  const hasIzin = useAuthStore((s) => s.hasIzin);

  const aktifModuller = MODULLER
    .map((m) => {
      const a = ayarlar?.find((x) => x.modul === m.key);
      return { ...m, aktif: a?.aktif ?? true, sira: a?.sira ?? MODULLER.indexOf(m) };
    })
    .filter((m) => m.zorunlu || m.aktif)
    .filter((m) => {
      const izin = MODUL_IZIN[m.key];
      return !izin || hasIzin(izin);
    })
    .sort((a, b) => a.sira - b.sira);

  const ayarlarVisible =
    hasIzin('rol:goruntule') ||
    hasIzin('kullanici:goruntule') ||
    hasIzin('ozel-alan:goruntule');

  const content = (
    <>
      <div className="flex h-16 items-center justify-between border-b px-5">
        <span className="flex items-center gap-2">
          <img
            src="/zo/zed.png"
            alt=""
            className="h-8 w-auto select-none"
            draggable={false}
          />
          <span className="text-lg font-bold tracking-tight text-primary">
            {t('uygulama_adi')}
          </span>
        </span>
        {onMobileClose && (
          <button
            type="button"
            onClick={onMobileClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted md:hidden"
            aria-label="Menüyü kapat"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
        {aktifModuller.map(({ to, i18n, icon: Icon, label, key }) => (
          <NavLink
            key={key}
            to={to}
            onMouseEnter={() => prefetchRoute(to)}
            onFocus={() => prefetchRoute(to)}
            onTouchStart={() => prefetchRoute(to)}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )
            }
          >
            <Icon className="h-4 w-4" />
            {t(i18n, { defaultValue: label })}
          </NavLink>
        ))}
      </nav>
      <div className="space-y-1 border-t p-3">
        <NavLink
          to="/ai-sablonlar"
          onMouseEnter={() => prefetchRoute('/ai-sablonlar')}
          onFocus={() => prefetchRoute('/ai-sablonlar')}
          onTouchStart={() => prefetchRoute('/ai-sablonlar')}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )
          }
        >
          <Sparkles className="h-4 w-4" />
          AI Şablonlar
        </NavLink>
        {/* AI Maliyet CRM menüsünden bilinçli kaldırıldı (Selim, 2026-07-17):
            maliyet/gelir takibi ayrı Komuta Merkezi sitesinden yapılır.
            /admin/ai-maliyet rotası ve backend API'si yedek erişim olarak durur. */}
        <button
          onClick={() => setSihirbazAcik(true)}
          className="flex w-full items-center gap-3 rounded-md bg-amber-500 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-amber-600"
        >
          <LayoutGrid className="h-4 w-4" />
          Menü / Modül Sihirbazı
        </button>
        {ayarlarVisible && (
          <NavLink
            to="/ayarlar"
            onMouseEnter={() => prefetchRoute('/ayarlar')}
            onFocus={() => prefetchRoute('/ayarlar')}
            onTouchStart={() => prefetchRoute('/ayarlar')}
            className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Settings className="h-4 w-4" />
            {t('menu.ayarlar')}
          </NavLink>
        )}
        {/* Son kullanıcı için sade kredi sayacı — TL/tavan yok, sadece sayı */}
        <div className="px-3 pt-1">
          <AiKrediChip />
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden w-60 flex-col border-r bg-card md:flex">
        {content}
      </aside>

      {/* Mobil off-canvas drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={onMobileClose}
            aria-hidden="true"
          />
          <aside
            className="absolute left-0 top-0 flex h-full w-64 flex-col border-r bg-card shadow-xl animate-in slide-in-from-left"
            role="dialog"
            aria-modal="true"
            aria-label="Ana menü"
          >
            {content}
          </aside>
        </div>
      )}

      <ModulSihirbaziModal open={sihirbazAcik} onClose={() => setSihirbazAcik(false)} />
    </>
  );
}
